import React, { useState, useEffect } from 'react';
import { CloseIcon,} from '../../../../public/icons';
import { useToast } from '../../../../context/ToastContext';
import axios from 'axios';

const AddGiftTypeModal = ({ isOpen, onClose, onSubmit, loading = false, editingGiftType = null }) => {
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    basePrice: '',
    deliveryMode: 'physical',
    requireUpload: false,
    contentType: 'All media',
    leadTime: '',
    deviceSelection: false,
    accessMethod: 'QR Code',
    description: '',
    internalNotes: '',
    status: 'active'
  });

  const { error, success: showSuccess } = useToast();

  // Reset form when modal opens or editing gift type changes
  useEffect(() => {
    if (isOpen) {
      if (editingGiftType) {
        // Populate form with editing data
        setFormData({
          name: editingGiftType.name || '',
          basePrice: editingGiftType.basePrice || '',
          deliveryMode: editingGiftType.deliveryMode || 'physical',
          requireUpload: editingGiftType.requireUpload || false,
          contentType: editingGiftType.contentType || 'All media',
          leadTime: editingGiftType.leadTime || '',
          deviceSelection: editingGiftType.deviceSelection || false,
          accessMethod: editingGiftType.accessMethod || 'QR Code',
          description: editingGiftType.description || '',
          internalNotes: editingGiftType.internalNotes || '',
          status: editingGiftType.enabled ? 'active' : 'draft'
        });
      } else {
        // Reset form for new gift type
        setFormData({
          name: '',
          basePrice: '',
          deliveryMode: 'physical',
          requireUpload: false,
          contentType: 'All media',
          leadTime: '',
          deviceSelection: false,
          accessMethod: 'QR Code',
          description: '',
          internalNotes: '',
          status: 'active'
        });
      }
    }
  }, [isOpen, editingGiftType]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = () => {
    // Validate form
    if (!formData.name.trim()) {
      error('Gift type name is required');
      return;
    }

    if (!formData.basePrice || parseFloat(formData.basePrice) <= 0) {
      error('Valid base price is required');
      return;
    }

    // Determine icon based on content type
    let icon = 'file';
    if (formData.contentType?.toLowerCase().includes('audio')) icon = 'music';
    else if (formData.contentType?.toLowerCase().includes('image') || 
             formData.contentType?.toLowerCase().includes('gallery')) icon = 'gallery';

    const giftTypeData = {
      name: formData.name.trim(),
      basePrice: parseFloat(formData.basePrice),
      deliveryMode: formData.deliveryMode,
      requireUpload: formData.requireUpload,
      contentType: formData.contentType,
      leadTime: formData.leadTime ? parseInt(formData.leadTime) : null,
      deviceSelection: formData.deviceSelection,
      accessMethod: formData.deliveryMode === 'digital' ? formData.accessMethod : null,
      description: formData.description.trim() || '',
      internalNotes: formData.internalNotes.trim() || '',
      icon: icon,
      enabled: formData.status === 'active',
      deliveryWindow: {
        min: formData.leadTime ? parseInt(formData.leadTime) : 0,
        max: formData.leadTime ? parseInt(formData.leadTime) + 3 : 7,
        unit: 'days'
      },
      customization: {
        allowPhoto: formData.contentType === 'All media' || formData.contentType === 'image',
        allowMessage: true,
        allowAudio: formData.contentType === 'All media' || formData.contentType === 'audio',
        allowVideo: formData.contentType === 'All media' || formData.contentType === 'video'
      }
    };

    console.log('📤 [AddGiftTypeModal] Submitting gift type:', JSON.stringify(giftTypeData, null, 2));
    onSubmit(giftTypeData);
  };

  const isSubmitEnabled = formData.name.trim() && 
                          formData.basePrice && 
                          parseFloat(formData.basePrice) > 0;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300"
        onClick={onClose}
      />
      
      <div className="fixed right-0 top-0 h-full w-full md:w-[60%] text-left lg:w-[50%] xl:w-[40%] bg-[#F9F6F2] z-[999] overflow-y-auto transition-transform duration-300 ease-in-out">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between pb-5 pt-4 border-b mx-6 border-[#DBDBDB]">
            <div className="text-left">
              <h2 className="text-2xl font-bold text-[#1C2A46]">
                {editingGiftType ? 'Edit Gift Type' : 'Add New Gift Type'}
              </h2>
              <p className="text-[#6B635D] text-sm mt-1">
                Define what kinds of gifts can be scheduled for future delivery
              </p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB] disabled:opacity-50"
              disabled={loading || uploadingImage}
            >
              <CloseIcon className="w-6 h-6 text-[#1C2A46]" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {/* Gift Type Name */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Gift Type Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g, custom video message"
                className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:opacity-50"
                disabled={loading || uploadingImage}
              />
            </div>

            {/* Base Price */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Base price (₦) *
              </label>
              <input
                type="number"
                name="basePrice"
                value={formData.basePrice}
                onChange={handleChange}
                placeholder="15000"
                min="0"
                step="0.01"
                className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:opacity-50"
                disabled={loading || uploadingImage}
              />
            </div>

            {/* Delivery Mode */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Delivery Mode *
              </label>
              <div className="relative">
                <select
                  name="deliveryMode"
                  value={formData.deliveryMode}
                  onChange={handleChange}
                  className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] appearance-none focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:opacity-50 pr-10"
                  disabled={loading || uploadingImage}
                >
                  <option value="physical">Physical</option>
                  <option value="digital">Digital</option>
                  <option value="hybrid">Hybrid</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                  <svg className="w-5 h-5 text-[#6B635D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Require Content Upload - Checkbox */}
            <div className="mb-6">
              <div className="flex items-start">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="requireUpload"
                    checked={formData.requireUpload}
                    onChange={handleChange}
                    disabled={loading || uploadingImage}
                    className="sr-only peer"
                  />
                  <div className={`w-6 h-6 border-2 border-[#1C2A46] rounded peer-checked:bg-[#1C2A46] peer-checked:border-[#1C2A46] flex items-center justify-center ${
                    loading || uploadingImage ? 'opacity-50 cursor-not-allowed' : ''
                  }`}>
                    {formData.requireUpload && (
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </label>
                <div className="ml-2">
                  <label htmlFor="requireUpload" className="block text-sm font-medium text-[#1C2A46] cursor-pointer">
                    Requires content upload?
                  </label>
                  <p className="text-sm text-gray-600">
                    User must upload files/content
                  </p>
                </div>
              </div>
            </div>

            {/* Content Type */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Content type
              </label>
              <div className="relative">
                <select
                  name="contentType"
                  value={formData.contentType}
                  onChange={handleChange}
                  className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] appearance-none focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:opacity-50 pr-10"
                  disabled={loading || uploadingImage}
                >
                  <option value="All media">All media</option>
                  <option value="audio">Audio only</option>
                  <option value="video">Video only</option>
                  <option value="image">Image only</option>
                  <option value="text">Text only</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                  <svg className="w-5 h-5 text-[#6B635D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Determines what customers can upload later.
              </p>
            </div>

            {/* Lead Time */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Lead Time (days)
              </label>
              <input
                type="number"
                name="leadTime"
                value={formData.leadTime}
                onChange={handleChange}
                placeholder="2"
                min="0"
                className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:opacity-50"
                disabled={loading || uploadingImage}
              />
              <p className="mt-2 text-sm text-gray-600">
                Days needed to prepare and fulfil this gift (0 for instant delivery)
              </p>
            </div>

            {/* Device Selection - Only for physical/hybrid */}
            {(formData.deliveryMode === 'physical' || formData.deliveryMode === 'hybrid') && (
              <div className="mb-6">
                <div className="flex items-start">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="deviceSelection"
                      checked={formData.deviceSelection}
                      onChange={handleChange}
                      disabled={loading || uploadingImage}
                      className="sr-only peer"
                    />
                    <div className={`w-6 h-6 border-2 border-[#1C2A46] rounded peer-checked:bg-[#1C2A46] peer-checked:border-[#1C2A46] flex items-center justify-center ${
                      loading || uploadingImage ? 'opacity-50 cursor-not-allowed' : ''
                    }`}>
                      {formData.deviceSelection && (
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </label>
                  <div className="ml-2">
                    <label htmlFor="deviceSelection" className="block text-sm font-medium text-[#1C2A46] cursor-pointer">
                      Device selection required
                    </label>
                    <p className="text-sm text-gray-600">
                      e.g., teddy bear, clock
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Access Method - Only for digital */}
            {formData.deliveryMode === 'digital' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                  Access Method
                </label>
                <div className="relative">
                  <select
                    name="accessMethod"
                    value={formData.accessMethod}
                    onChange={handleChange}
                    className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] appearance-none focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:opacity-50 pr-10"
                    disabled={loading || uploadingImage}
                  >
                    <option value="QR Code">QR Code</option>
                    <option value="Link">Link</option>
                    <option value="Email">Email</option>
                    <option value="SMS">SMS</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                    <svg className="w-5 h-5 text-[#6B635D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            )}

            {/* Status Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Status *
              </label>
              <div className="relative">
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] appearance-none focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:opacity-50 pr-10"
                  disabled={loading || uploadingImage}
                >
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                  <svg className="w-5 h-5 text-[#6B635D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Set the initial status for this gift type.
              </p>
            </div>

            {/* Internal Notes */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Internal Notes
              </label>
              <textarea
                name="internalNotes"
                value={formData.internalNotes}
                onChange={handleChange}
                rows={4}
                placeholder="Add note to guide the fulfilment team on how to handle this gift type..."
                className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent resize-none disabled:opacity-50"
                disabled={loading || uploadingImage}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 ">
            <button 
              onClick={handleSubmit}
              disabled={!isSubmitEnabled || loading || uploadingImage}
              className={`w-full px-6 py-3 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isSubmitEnabled && !loading && !uploadingImage
                  ? 'bg-[#D2A146] text-white hover:bg-[#C49660]'
                  : 'bg-gray-300 text-gray-500'
              }`}
            >
              {loading ? 'Saving...' : uploadingImage ? 'Uploading...' : (editingGiftType ? 'Update Gift Type' : 'Add Gift Type')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddGiftTypeModal;